import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import UbicacionSelector from '../../components/UbicacionSelector';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO, PARAMS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import remitoCompraService from '../../services/remitoCompraService';
import comprasSeguimientoService, {
  CompraPendienteMaster,
  esProductoInventariado,
  filtrarCompraPendientesPorBusqueda,
  filtrarCompraPendientesPorFechaEmision,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
  precioRemitoDesdePendienteMaster,
} from '../../services/comprasSeguimientoService';
import { getApiErrorMessage } from '../../utils/apiError';

interface RemitoCompraItemForm {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc: number;
  notas: string;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
}

interface RemitoCompraFormularioForm {
  nroSuc: number;
  letra: string;
  nroComprobante: number;
  nroProveedor: number;
  nombre?: string;
  cuit?: string;
  descripcion?: string;

  fechaEmision: string;
  fechaVencim?: string;

  nroUbicacionDestino?: number;
  nroFormularioFactura?: number;
}

/** Factura de compra (tipos 1/2) cuando el pendiente apunta directamente al comprobante FC. */
const nroFormularioFacturaDesdePendiente = (p: CompraPendienteMaster): number | undefined => {
  const tipo = String(p.nroTipoFormularioMaster ?? '').trim();
  if (tipo === '1' || tipo === '2') {
    const nro = Number(p.nroFormularioMaster);
    return nro > 0 ? nro : undefined;
  }
  return undefined;
};

const defaultItem: RemitoCompraItemForm = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  precioUnitario: 0,
  porcDesc: 0,
  notas: '',
};

const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

const normalizeCodigoArticulo = (codigo: string | null | undefined): string =>
  String(codigo ?? '').trim().toLowerCase();

const isPendienteEnRemito = (
  pendiente: CompraPendienteMaster,
  remitoItems: RemitoCompraItemForm[],
): boolean =>
  remitoItems.some(
    (it) =>
      it.nroFormularioMaster === pendiente.nroFormularioMaster &&
      it.nroItemMaster === pendiente.nroItemMaster,
  );

const RemitoCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const compraPendienteFromNav = (location.state as { compraPendiente?: CompraPendienteMaster } | null)?.compraPendiente;
  const compraPendienteNavAppliedRef = useRef(false);
  const agregarPendienteRef = useRef<(p: CompraPendienteMaster) => Promise<void>>(async () => {});

  const [formulario, setFormulario] = useState<RemitoCompraFormularioForm>({
    nroSuc: 0,
    letra: 'R',
    nroComprobante: 0,
    nroProveedor: 0,
    nombre: '',
    cuit: '',
    descripcion: '',
    fechaEmision: todayIsoDate(),
    fechaVencim: todayIsoDate(),
    nroUbicacionDestino: undefined,
  });

  const [proveedorNombre, setProveedorNombre] = useState('');
  const [ubicacionNombre, setUbicacionNombre] = useState('');

  const [items, setItems] = useState<RemitoCompraItemForm[]>([]);
  const [saving, setSaving] = useState(false);

  // Producto search modal
  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productos, setProductos] = useState<any[]>([]);
  const [productosPage, setProductosPage] = useState(0); // 0-based
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);
  const [openPendientes, setOpenPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<CompraPendienteMaster[]>([]);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [pendientesBusqueda, setPendientesBusqueda] = useState('');
  const [pendientesFechaDesde, setPendientesFechaDesde] = useState('');
  const [pendientesFechaHasta, setPendientesFechaHasta] = useState('');
  const [soloInventariados, setSoloInventariados] = useState(true);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);

  const codigosArticuloEnRemito = useMemo(() => {
    const codigos = new Set<string>();
    items.forEach((it) => {
      const codigo = normalizeCodigoArticulo(it.codigoArticulo);
      if (codigo) codigos.add(codigo);
    });
    return codigos;
  }, [items]);

  const productosDisponibles = useMemo(
    () =>
      productos.filter((p) => {
        const codigo = normalizeCodigoArticulo(p.codigoArticulo || p.codigo);
        return codigo && !codigosArticuloEnRemito.has(codigo);
      }),
    [productos, codigosArticuloEnRemito],
  );

  const pendientesDisponibles = useMemo(
    () => pendientes.filter((p) => !isPendienteEnRemito(p, items)),
    [pendientes, items],
  );

  const pendientesFiltrados = useMemo(() => {
    let rows = pendientesDisponibles;
    if (soloInventariados) {
      rows = rows.filter((p) => esProductoInventariado(p.invetariado));
    }
    rows = filtrarCompraPendientesPorFechaEmision(rows, pendientesFechaDesde, pendientesFechaHasta);
    return filtrarCompraPendientesPorBusqueda(rows, pendientesBusqueda);
  }, [pendientesDisponibles, soloInventariados, pendientesBusqueda, pendientesFechaDesde, pendientesFechaHasta]);

  const handleFormularioChange = (updates: Partial<RemitoCompraFormularioForm>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const handleItemChange = (idx: number, updates: Partial<RemitoCompraItemForm>) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...updates };
      return next;
    });
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const addItem = () => setItems((prev) => [...prev, { ...defaultItem }]);

  const handleProveedorSelect = (prov: any) => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: prov.nroProveedor,
      nombre: prov.nombre,
      cuit: prov.cuit,
    }));
    setProveedorNombre(prov.nombre || '');
  };

  const handleProveedorClear = () => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: 0,
      nombre: '',
      cuit: '',
    }));
    setProveedorNombre('');
  };

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) loadProductos();
  }, [openProductSearch, loadProductos]);

  useEffect(() => {
    if (compraPendienteNavAppliedRef.current) return;
    if (!compraPendienteFromNav) return;

    compraPendienteNavAppliedRef.current = true;
    const p = compraPendienteFromNav;
    const np = p.nroProveedor;
    if (np && np > 0) {
      setFormulario((prev) => ({
        ...prev,
        nroProveedor: np,
        nombre: p.nombreProveedor || prev.nombre,
      }));
      setProveedorNombre(p.nombreProveedor || '');
    }
    void agregarPendienteRef.current(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compraPendienteFromNav]);

  const handleSelectProduct = (p: any) => {
    const idx = productSearchIndex;
    if (idx === null) return;
    const codigo = p.codigoArticulo || p.codigo || '';
    const desc = p.descripcion || p.descripcionArticulo || '';
    const costo = Number(p.costo) || 0;
    setItems((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        codigoArticulo: String(codigo),
        descripcion: String(desc),
        precioUnitario: costo,
      };
      return next;
    });
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const handleCodigoBlurOrEnter = useCallback(
    async (idx: number, codigoValue: string) => {
      const codigo = (codigoValue || '').trim();
      if (!codigo) return;
      try {
        const p = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
        const desc = (p as any).descripcion || (p as any).descripcionArticulo || '';
        const costo = Number((p as any).costo) || Number((p as any).costoBrutoUnitario) || 0;
        handleItemChange(idx, { codigoArticulo: codigo, descripcion: desc, precioUnitario: costo });
      } catch {
        enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
      }
    },
    [enqueueSnackbar]
  );

  const handleSubmit = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe ingresar una sucursal válida', { variant: 'warning' });
      return;
    }
    if (!formulario.letra?.trim()) {
      enqueueSnackbar('Debe seleccionar una letra', { variant: 'warning' });
      return;
    }
    if (!formulario.nroComprobante || formulario.nroComprobante <= 0) {
      enqueueSnackbar('Debe ingresar un número de comprobante válido', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionDestino || formulario.nroUbicacionDestino <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación destino válida', { variant: 'warning' });
      return;
    }

    const itemsValidos = items.filter(
      (it) => (it.codigoArticulo || '').trim().length > 0 && Number(it.cantidad) > 0
    );

    if (itemsValidos.length === 0) {
      enqueueSnackbar('Debe agregar al menos un ítem válido (código y cantidad > 0)', { variant: 'warning' });
      return;
    }

    const payload = {
      formulario: {
        nroSuc: Number(formulario.nroSuc),
        letra: formulario.letra?.trim() || 'A',
        nroComprobante: Number(formulario.nroComprobante),
        nroProveedor: Number(formulario.nroProveedor),
        nombre: formulario.nombre || undefined,
        cuit: formulario.cuit || undefined,
        descripcion: formulario.descripcion?.trim() || undefined,
        fechaEmision: formulario.fechaEmision || todayIsoDate(),
        fechaVencim: formulario.fechaVencim || undefined,
        nroUbicacionDestino: formulario.nroUbicacionDestino,
        nroFormularioFactura:
          formulario.nroFormularioFactura && formulario.nroFormularioFactura > 0
            ? formulario.nroFormularioFactura
            : undefined,
      },
      items: itemsValidos.map((it) => ({
        codigoArticulo: it.codigoArticulo.trim(),
        descripcion: it.descripcion?.trim() || undefined,
        cantidad: Number(it.cantidad),
        precioUnitario: Number(it.precioUnitario),
        porcDesc: Number(it.porcDesc ?? 0),
        notas: (() => {
          const n = it.notas?.trim();
          return n ? n.slice(0, 500) : undefined;
        })(),
        nroFormularioMaster: it.nroFormularioMaster,
        nroItemMaster: it.nroItemMaster,
      })),
    };

    try {
      setSaving(true);
      const created = await remitoCompraService.createRemitoCompra(payload as any);
      enqueueSnackbar('Remito de compra creado correctamente', { variant: 'success' });
      navigate(`/compras/remitos/${created.nroFormulario}`);
    } catch (error: unknown) {
      const msg = getApiErrorMessage(error, 'Error al crear el remito de compra');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const openBuscarPendientes = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setPendientesBusqueda('');
    setPendientesFechaDesde('');
    setPendientesFechaHasta('');
    setSoloInventariados(true);
    setOpenPendientes(true);
    setLoadingPendientes(true);
    try {
      const data = await comprasSeguimientoService.getPendientesMaster(formulario.nroProveedor, true);
      const rows = (data || []).filter((p) => (Number(p.pendienteRecibir) || 0) > 0);
      setPendientes(rows);
      if (rows.length === 0) {
        enqueueSnackbar('No hay líneas con pendiente de recibir para este proveedor', { variant: 'info' });
      }
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar pendientes'), { variant: 'error' });
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const agregarPendiente = async (p: CompraPendienteMaster) => {
    const pendienteRecibir = Number(p.pendienteRecibir) || 0;
    const cantidadSugerida = pendienteRecibir > 0 ? pendienteRecibir : 1;
    let { precioUnitario, porcDesc } = precioRemitoDesdePendienteMaster(p);
    if (precioUnitario <= 0 && (p.codigoArticulo || '').trim()) {
      try {
        const pr = await productoService.getProducto((p.codigoArticulo || '').trim(), OPTIONS_PRODUCTO_OPERATIVO);
        precioUnitario = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
        porcDesc = 0;
      } catch {
        /* sin precio en OC ni maestro */
      }
    }
    setItems((prev) => {
      const exists = prev.some(
        (it) =>
          it.nroFormularioMaster === p.nroFormularioMaster &&
          it.nroItemMaster === p.nroItemMaster
      );
      if (exists) return prev;
      return [
        ...prev,
        {
          ...defaultItem,
          codigoArticulo: p.codigoArticulo || '',
          descripcion: p.descripcionArticulo || '',
          cantidad: cantidadSugerida,
          precioUnitario,
          porcDesc,
          nroFormularioMaster: p.nroFormularioMaster,
          nroItemMaster: p.nroItemMaster,
        },
      ];
    });
    const nroFc = nroFormularioFacturaDesdePendiente(p);
    if (nroFc) {
      setFormulario((f) => ({ ...f, nroFormularioFactura: nroFc }));
    }
  };
  agregarPendienteRef.current = agregarPendiente;

  const agregarTodosPendientes = async () => {
    if (!pendientesFiltrados.length) return;

    const keysExistentes = new Set(
      items.map((it) => `${it.nroFormularioMaster}-${it.nroItemMaster}`),
    );
    const nuevosItems: RemitoCompraItemForm[] = [];
    let agregadas = 0;
    let omitidas = 0;
    let nroFc: number | undefined;

    for (const p of pendientesFiltrados) {
      const key = `${p.nroFormularioMaster}-${p.nroItemMaster}`;
      if (keysExistentes.has(key)) {
        omitidas += 1;
        continue;
      }

      const pendienteRecibir = Number(p.pendienteRecibir) || 0;
      const cantidadSugerida = pendienteRecibir > 0 ? pendienteRecibir : 1;
      let { precioUnitario, porcDesc } = precioRemitoDesdePendienteMaster(p);
      if (precioUnitario <= 0 && (p.codigoArticulo || '').trim()) {
        try {
          const pr = await productoService.getProducto((p.codigoArticulo || '').trim(), OPTIONS_PRODUCTO_OPERATIVO);
          precioUnitario = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
          porcDesc = 0;
        } catch {
          /* sin precio en OC ni maestro */
        }
      }

      nuevosItems.push({
        ...defaultItem,
        codigoArticulo: p.codigoArticulo || '',
        descripcion: p.descripcionArticulo || '',
        cantidad: cantidadSugerida,
        precioUnitario,
        porcDesc,
        nroFormularioMaster: p.nroFormularioMaster,
        nroItemMaster: p.nroItemMaster,
      });
      keysExistentes.add(key);
      agregadas += 1;

      const fc = nroFormularioFacturaDesdePendiente(p);
      if (fc) nroFc = fc;
    }

    if (agregadas === 0) {
      enqueueSnackbar('No se pudo agregar ninguna línea desde pendientes', { variant: 'info' });
      return;
    }

    setItems((prev) => [...prev, ...nuevosItems]);
    if (nroFc) {
      setFormulario((f) => ({ ...f, nroFormularioFactura: nroFc }));
    }
    enqueueSnackbar(
      omitidas > 0
        ? `Se agregaron ${agregadas} líneas. ${omitidas} quedaron omitidas.`
        : `Se agregaron ${agregadas} líneas desde pendientes.`,
      { variant: 'success' },
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            Nuevo Remito de Compra
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <ProveedorSelectorField
                label="Proveedor"
                required
                value={
                  formulario.nroProveedor > 0 && proveedorNombre
                    ? {
                        nroProveedor: formulario.nroProveedor,
                        nombre: proveedorNombre,
                        cuit: formulario.cuit || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
              />
            </Grid>


            <Grid item xs={6} md={2}>
              <SearchableSelect
                label="Letra"
                required
                value={formulario.letra || 'R'}
                onChange={(v) => handleFormularioChange({ letra: v ?? 'R' })}
                options={[
                  { value: 'R', label: 'R' },
                  { value: 'X', label: 'X' },
                ]}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Sucursal"
                type="text"
                value={String(formulario.nroSuc ?? 0).padStart(5, '0')}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  const n = parseInt(v, 10);
                  const next =
                    v === '' || isNaN(n) ? 0 : Math.min(99999, Math.max(0, n));
                  handleFormularioChange({ nroSuc: next });
                }}
                inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
                required
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Nro. Comprobante"
                type="text"
                value={String(formulario.nroComprobante ?? 0).padStart(8, '0')}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  const n = parseInt(v, 10);
                  const next =
                    v === '' || isNaN(n) ? 0 : Math.min(99999999, Math.max(0, n));
                  handleFormularioChange({ nroComprobante: next });
                }}
                inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric' }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                value={formulario.fechaEmision}
                onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Fecha vencimiento"
                type="date"
                value={formulario.fechaVencim || ''}
                onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <UbicacionSelector
                label="Ubicación destino"
                size="medium"
                value={ubicacionNombre}
                onChange={(label, nro) => {
                  setUbicacionNombre(label);
                  handleFormularioChange({ nroUbicacionDestino: nro });
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Observaciones"
                value={formulario.descripcion || ''}
                onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Acción
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      Sin ítems. Use &quot;Agregar item&quot; para añadir líneas.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((it, idx) => (
                  <TableRow key={idx} hover sx={{ '& > td': { verticalAlign: 'top' } }}>
                    <TableCell sx={{ width: 170 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={it.codigoArticulo}
                        placeholder="Código"
                        onChange={(e) => handleItemChange(idx, { codigoArticulo: e.target.value })}
                        onBlur={(e) => handleCodigoBlurOrEnter(idx, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setProductSearchIndex(idx);
                                  setOpenProductSearch(true);
                                }}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={1}
                        value={it.descripcion}
                        placeholder="Descripción"
                        onChange={(e) => handleItemChange(idx, { descripcion: e.target.value })}
                        sx={{
                          '& .MuiInputBase-inputMultiline': {
                            overflow: 'hidden',
                          },
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ width: 140 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={Number.isFinite(it.cantidad) ? it.cantidad : 0}
                        inputProps={{ min: 0, step: 0.01 }}
                        onChange={(e) => handleItemChange(idx, { cantidad: parseFloat(e.target.value) || 0 })}
                      />
                    </TableCell>

                    <TableCell sx={{ minWidth: 220 }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={it.notas}
                        placeholder="Notas"
                        inputProps={{ maxLength: 500 }}
                        onChange={(e) => handleItemChange(idx, { notas: e.target.value.slice(0, 500) })}
                      />
                    </TableCell>

                    <TableCell sx={{ width: 90 }} align="center">
                      <IconButton size="small" onClick={() => removeItem(idx)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3, justifyContent: 'flex-start' }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={addItem}>
              Agregar item
            </Button>
            <Button variant="outlined" onClick={openBuscarPendientes} sx={{ color: '#1976d2', borderColor: '#1976d2' }}>
              Buscar pendientes
            </Button>
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={() => navigate('/compras/remitos')} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={handleSubmit} disabled={saving} sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={openProductSearch}
        onClose={() => {
          setOpenProductSearch(false);
          setProductSearchIndex(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Buscar artículo</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setProductosPage(0);
                  loadProductos();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setProductosPage(0);
                loadProductos();
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2' }}
            >
              Buscar
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productosDisponibles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      {productos.length === 0
                        ? 'No se encontraron artículos.'
                        : 'Los artículos de esta página ya están en el remito.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  productosDisponibles.map((p) => (
                  <TableRow key={p.codigoArticulo || p.codigo || ''} hover>
                    <TableCell>{p.codigoArticulo || p.codigo || '-'}</TableCell>
                    <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => handleSelectProduct(p)}>
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openPendientes} onClose={() => setOpenPendientes(false)} maxWidth="md" fullWidth>
        <DialogTitle>Buscar pendientes</DialogTitle>
        <DialogContent>
          {loadingPendientes ? (
            <CircularProgress size={22} />
          ) : (
            <>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 2, mt: 1 }}
                flexWrap="wrap"
                useFlexGap
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por comprobante, código o descripción..."
                  value={pendientesBusqueda}
                  onChange={(e) => setPendientesBusqueda(e.target.value)}
                  sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Fecha emisión desde"
                  type="date"
                  value={pendientesFechaDesde}
                  onChange={(e) => setPendientesFechaDesde(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 170 } }}
                />
                <TextField
                  label="Fecha emisión hasta"
                  type="date"
                  value={pendientesFechaHasta}
                  onChange={(e) => setPendientesFechaHasta(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: { xs: '100%', sm: 170 } }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={soloInventariados}
                      onChange={(e) => setSoloInventariados(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Solo inventariados"
                  sx={{ ml: 0, whiteSpace: 'nowrap' }}
                />
              </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha emisión</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cant. pedida</TableCell>
                    <TableCell align="right">Pend. facturar</TableCell>
                    <TableCell align="right">Pend. recibir</TableCell>
                    <TableCell align="center" sx={{ width: 44 }} />
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientesFiltrados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                        {pendientes.length === 0
                          ? 'No hay líneas con pendiente de recibir para este proveedor.'
                          : pendientesDisponibles.length === 0
                            ? 'Las líneas pendientes ya fueron agregadas a este remito.'
                            : 'No hay pendientes que coincidan con los filtros aplicados.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendientesFiltrados.map((p) => (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}</TableCell>
                      <TableCell>{p.codigoArticulo} - {p.descripcionArticulo}</TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell align="right">{p.pendienteFacturar}</TableCell>
                      <TableCell align="right">{p.pendienteRecibir}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver documentos facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => void agregarPendiente(p)}>
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => void agregarTodosPendientes()}
            disabled={pendientesFiltrados.length === 0 || loadingPendientes}
          >
            Agregar todos
          </Button>
          <Button onClick={() => setOpenPendientes(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />
    </Box>
  );
};

export default RemitoCompraForm;

